import { Injectable } from '@angular/core';
import { LocalStorageService } from '../cache';
import { FeatureFlags, FeatureFlagsService } from './FeatureFlags.service';
import { HeaderService } from './Header.service';

@Injectable({ providedIn: 'root' })
export class DemoDataService {
    /**
     * this model is mapped to Lin Leader in all the demoServices in HireAPI + InspireAPI + OrgAPI
     * Be careful refactoring this reference
     */
    public static readonly LIN_LEADER = 'ac7276ec-4977-444e-9594-8f48e527d110';
    private static readonly ACTOR_OVERRIDE_KEY = 'UserAgentWantsDemoData';
    private readonly demoDataLocalStateDefault = false;

    constructor(
        private headerService: HeaderService,
        private featureFlagService: FeatureFlagsService,
        private storageService: LocalStorageService
    ) {
        this.initActorOverride(this.demoDataLocalStateDefault);
    }

    /**
     * Call this method in `app-bootstrap-helpers.service.ts` to initialize the demo experience given that isDemoData is `true`
     */
    async applyDemoDataExperience(): Promise<void> {
        await this.ensureFeatureFlagIsReady();
        this.headerService.setIsDemoData(true);
    }

    /**
     * Useful to determine if the demo experience is enabled for the currently logged in user
     * The intent is to show the UI elements that allows the user to override the experience if they want
     *
     * @returns the state of `FeatureFlags.enableSampleDataExperience`
     */
    isDemoEnabledFeatureFlagPrecedence(): boolean {
        return this.isDemoDataFlagEnabled();
    }

    /**
     * Useful to determine if the demo experience is enabled for the currently logged in user
     * The function will respect the client actor's override (session storage)
     *
     * @returns the state of `FeatureFlags.enableSampleDataExperience` || `ACTOR_OVERRIDE_KEY`
     */
    isDemoEnabled(): boolean {
        const hasFF = this.isDemoDataFlagEnabled();
        if (!hasFF) {
            // override doesn't matter if we don't have the feature flag
            return false;
        }

        return this.actorWantsDemoData();
    }

    /**
     * The User Actor wants to set the demo experience state
     *
     * @param state
     */
    setDemoDataLocalState(state: boolean): void {
        this.storageService.setItem(
            DemoDataService.ACTOR_OVERRIDE_KEY,
            state,
            0
        );

        this.setDemoDataExperience(state);
    }

    private setDemoDataExperience(state: boolean): void {
        this.headerService.setIsDemoData(state);
    }

    /**
     * We have to get the flag into the system the very first time we bootstrap this whole situation.
     * Don't need to do anything after the flag exists.
     *
     * @param state
     */
    private initActorOverride(state: boolean) {
        const actorOverrideWasInitialized = this.storageService.getItem(
            DemoDataService.ACTOR_OVERRIDE_KEY
        );

        if (actorOverrideWasInitialized === undefined) {
            this.setDemoDataLocalState(state);
        }
    }

    private actorWantsDemoData(): boolean {
        // if undefined force false
        return !!this.storageService.getItem(
            DemoDataService.ACTOR_OVERRIDE_KEY
        );
    }

    private isDemoDataFlagEnabled(): boolean {
        try {
            return this.featureFlagService.getByFlag(
                FeatureFlags.enableSampleDataExperience
            );
        } catch (error) {
            console.error(
                'DemoData Service Failed to Initialize, check configuration',
                error
            );
            // lowest layer, fallback to false in case anything bad happens
            return false;
        }
    }

    private async ensureFeatureFlagIsReady(): Promise<void> {
        // should be ready when this is called, but you know :shrug
        await this.featureFlagService.completionPromise;
    }
}
